import { Icon } from '../ui/Icon';
import { Button, Card, Skeleton } from '../ui/primitives';
import type { HomeLowStock } from '../../types/dashboard';
import { attentionView } from '../../utils/inicioView';
import { CardHead, CountChip, DoneBox, Tile } from './InicioParts';

const TITLE_ID = 'inicio-attention-title';

/**
 * Rediseño · Bloque C — «Necesita tu atención» (P-030, P-022).
 *
 * Lleva lo que el resumen del Inicio ya calcula: los insumos por agotarse, con sus mismos días
 * estimados y sus mismas acciones («Registrar compra», «Ajustar inventario», «Ver inventario»).
 * En la maqueta hay un solo insumo, con sus dos acciones al lado; aquí puede haber cinco, así que
 * «Ver inventario» va una vez, al pie de la lista, en lugar de repetirse en cada renglón.
 * El diseño dibuja además un corte por revisar y un gasto por confirmar; ninguno existe como dato
 * (B0-11, B0-14) y no se dibujan. Lo que hay que decidir está en «Por resolver».
 *
 * `lowStock` en `null` es «todavía cargando»: la tarjeta guarda su sitio con marcadores, para que las
 * demás no cambien de lugar cuando llegue.
 */
export function InicioAttention({ lowStock, multiStore, onNavigate }: { lowStock: HomeLowStock | null; multiStore: boolean; onNavigate: (destination: string) => void }) {
  if (!lowStock) {
    return (
      <Card aria-busy="true" aria-labelledby={TITLE_ID} as="section">
        <CardHead id={TITLE_ID} title="Necesita tu atención" />
        <span className="n3-visually-hidden" role="status">Cargando…</span>
        <div className="n3-inicio__skeletons">
          <Skeleton height="64px" />
          <Skeleton height="64px" />
        </div>
      </Card>
    );
  }
  const view = attentionView(lowStock, multiStore);
  return (
    <Card aria-labelledby={TITLE_ID} as="section">
      <CardHead
        aside={view.count > 0 ? <CountChip count={view.count} label={view.count === 1 ? 'insumo por atender' : 'insumos por atender'} /> : undefined}
        id={TITLE_ID}
        title="Necesita tu atención"
      />
      {view.state === 'insufficient-history' && <p className="n3-inicio__support">{view.message}</p>}
      {view.state === 'clear' && (
        <DoneBox action={<Button className="n3-inicio__link" onClick={() => onNavigate('inventory/stock')} variant="ghost">{view.inventoryLabel}</Button>} text={view.message!} />
      )}
      {view.state === 'items' && (
        <>
          <ul className="n3-inicio__items">
            {view.items.map((item) => (
              <li className="n3-inicio__item" key={item.key}>
                <Tile icon="inventory" tone="warning" />
                <div className="n3-inicio__item-body">
                  <p className="n3-inicio__item-title">{item.title}</p>
                  {item.detail && <p className="n3-inicio__item-detail">{item.detail}</p>}
                  <Button className="n3-inicio__item-action" onClick={() => onNavigate(item.action.destination)} variant="tonal">{item.action.label}</Button>
                </div>
              </li>
            ))}
          </ul>
          <Button className="n3-inicio__link n3-inicio__more" iconEnd={<Icon name="chevron-right" size={16} />} onClick={() => onNavigate('inventory/stock')} variant="ghost">{view.inventoryLabel}</Button>
        </>
      )}
    </Card>
  );
}
